import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Button,
} from "@mui/material";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  getLocalVersion,
  fetchRemoteVersion,
  hasNewVersionAvailable,
  forceReloadApp,
  clearAppCaches,
} from "../../utils/version/versionManager";

const VersionContext = createContext(null);

export const VersionProvider = ({ children }) => {
  const localVersion = useRef(getLocalVersion()).current;
  const [remoteVersion, setRemoteVersion] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Executa atualização forçada com recarregamento
  const applyUpdate = useCallback(
    async (targetVersion) => {
      setIsUpdating(true);
      setFeedback({
        type: "info",
        message: `Atualizando para a versão v${targetVersion || localVersion.version}...`,
        autoHide: false,
      });

      if (process.env.NODE_ENV === "test") {
        await forceReloadApp(targetVersion || "");
        return;
      }

      // Aguarda 800ms para exibição visual do feedback ao usuário
      setTimeout(async () => {
        await forceReloadApp(targetVersion || "");
      }, 800);
    },
    [localVersion.version]
  );

  // Função central de verificação de atualizações
  const checkForUpdates = useCallback(
    async ({ manual = false, silent = false } = {}) => {
      if (isChecking || isUpdating) return;

      if (manual) {
        setIsChecking(true);
      }
      try {
        const remote = await fetchRemoteVersion();
        if (!remote) {
          if (manual) {
            setFeedback({
              type: "warning",
              message: "Não foi possível verificar atualizações no momento. Tente novamente mais tarde.",
              autoHide: true,
            });
          }
          return;
        }

        setRemoteVersion(remote);
        const updateAvailable = hasNewVersionAvailable(localVersion, remote);

        if (updateAvailable) {
          setHasUpdate(true);

          // Verifica se há upload de arquivos em andamento
          const isUploading =
            typeof window !== "undefined" && window.__IS_UPLOADING__ === true;

          if (isUploading) {
            setFeedback({
              type: "info",
              message: `Nova versão v${remote.version} disponível. O app será atualizado assim que o upload for concluído.`,
              autoHide: false,
            });
          } else {
            await applyUpdate(remote.version);
          }
        } else if (manual) {
          setFeedback({
            type: "success",
            message: `Você já está na versão mais recente (v${localVersion.version}).`,
            autoHide: true,
          });
        }
      } catch (error) {
        console.warn("[VersionContext] Erro ao verificar atualizações:", error);
      } finally {
        if (manual) {
          setIsChecking(false);
        }
      }
    },
    [isChecking, isUpdating, localVersion, applyUpdate]
  );

  // 1. Verificação na inicialização (mount)
  useEffect(() => {
    checkForUpdates({ silent: true });
  }, [checkForUpdates]);

  // 2. Verificação ao retornar ao app PWA (visibilidade e foco)
  useEffect(() => {
    if (process.env.NODE_ENV === "test") return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdates({ silent: true });
      }
    };

    const handleFocus = () => {
      checkForUpdates({ silent: true });
    };

    const handleOnline = () => {
      checkForUpdates({ silent: true });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, [checkForUpdates]);

  // 3. Verificação periódica em segundo plano (a cada 60 segundos)
  useEffect(() => {
    if (process.env.NODE_ENV === "test") return;

    const intervalId = setInterval(() => {
      checkForUpdates({ silent: true });
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [checkForUpdates]);

  // 4. Captura erros de carregamento de chunks dinâmicos (ChunkLoadError)
  useEffect(() => {
    const handleChunkError = (event) => {
      const errorMsg = (
        event?.message ||
        event?.reason?.message ||
        ""
      ).toLowerCase();
      if (
        errorMsg.includes("loading chunk") ||
        errorMsg.includes("chunkloaderror") ||
        errorMsg.includes("failed to fetch dynamically imported module")
      ) {
        console.warn(
          "[VersionContext] ChunkLoadError detectado. Forçando atualização para versão mais recente..."
        );
        clearAppCaches().then(() => {
          window.location.reload();
        });
      }
    };

    window.addEventListener("error", handleChunkError);
    window.addEventListener("unhandledrejection", handleChunkError);

    return () => {
      window.removeEventListener("error", handleChunkError);
      window.removeEventListener("unhandledrejection", handleChunkError);
    };
  }, []);

  // 5. Se houver atualização pendente e o upload terminar, dispara o update
  useEffect(() => {
    if (!hasUpdate || isUpdating || process.env.NODE_ENV === "test") return;

    const checkUploadCompletion = setInterval(() => {
      const isUploading =
        typeof window !== "undefined" && window.__IS_UPLOADING__ === true;
      if (!isUploading && remoteVersion?.version) {
        clearInterval(checkUploadCompletion);
        applyUpdate(remoteVersion.version);
      }
    }, 2000);

    return () => clearInterval(checkUploadCompletion);
  }, [hasUpdate, isUpdating, remoteVersion, applyUpdate]);

  const handleCloseFeedback = (_, reason) => {
    if (reason === "clickaway" && !feedback?.autoHide) return;
    setFeedback(null);
  };

  const contextValue = {
    localVersion,
    remoteVersion,
    hasUpdate,
    isChecking,
    isUpdating,
    checkForUpdates,
    applyUpdate,
  };

  return (
    <VersionContext.Provider value={contextValue}>
      {children}
      {feedback && (
        <Snackbar
          open={Boolean(feedback)}
          autoHideDuration={feedback.autoHide ? 4000 : null}
          onClose={handleCloseFeedback}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={feedback.type || "info"}
            onClose={feedback.autoHide ? handleCloseFeedback : undefined}
            icon={
              isUpdating ? (
                <CircularProgress size={18} color="inherit" />
              ) : feedback.type === "success" ? (
                <CheckCircleOutlineIcon fontSize="inherit" />
              ) : (
                <SystemUpdateAltIcon fontSize="inherit" />
              )
            }
            action={
              hasUpdate && !isUpdating ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => applyUpdate(remoteVersion?.version)}
                >
                  Atualizar Agora
                </Button>
              ) : undefined
            }
            sx={{
              alignItems: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              borderRadius: "8px",
              fontWeight: 500,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {feedback.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      )}
    </VersionContext.Provider>
  );
};

export const useVersion = () => {
  const context = useContext(VersionContext);
  if (!context) {
    // Retorna valores padrão caso usado fora do provider (ex: testes simples)
    const local = getLocalVersion();
    return {
      localVersion: local,
      remoteVersion: null,
      hasUpdate: false,
      isChecking: false,
      isUpdating: false,
      checkForUpdates: () => Promise.resolve(),
      applyUpdate: () => Promise.resolve(),
    };
  }
  return context;
};

export default VersionContext;
